"use client";

import React from "react";
import { ArrowRightIcon, CheckIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import {
  getPresetPresentationType,
  isCreditPresetAction,
  isVibeBundlePreset,
} from "@/lib/agent/presetPresentation";
import LoadingSpinner from "@/components/ui/loading-spinner";
import {
  REFERENCE_IMAGE_FORMAT_LABEL,
  REFERENCE_IMAGE_MAX_COUNT,
} from "@/lib/create/referenceImageUpload";
import { PresetOption } from "@/types/createAgent";
import {
  getBadgeClass,
  getIconClass,
  getPresetChoiceVisual,
  getPresetDisplayLabel,
  getPresetMeta,
} from "./presetMeta";

export function PresetWidget({
  preset,
  isProcessing,
  isDisabled,
  isSelected,
  onClick,
  isSelector = false,
  compact = false,
  className,
}: {
  preset: PresetOption;
  isProcessing: boolean;
  isDisabled: boolean;
  isSelected: boolean;
  onClick: () => void;
  isSelector?: boolean;
  compact?: boolean;
  className?: string;
}) {
  const meta = getPresetMeta(preset);
  const isCredit = isCreditPresetAction(preset.actionId);
  // Upload rows open the OS file picker on click — an act, not a selection —
  // so they carry the act arrow even inside selector lists.
  const isUploadChoice = getPresetPresentationType(preset) === "upload_choice";
  const showsRadio = isSelector && !isUploadChoice;
  const choiceVisual = getPresetChoiceVisual(preset);
  const displayLabel = getPresetDisplayLabel(preset);
  const isVibePreset = isVibeBundlePreset(preset) && choiceVisual;
  const uploadHintId = React.useId();

  if (isUploadChoice) {
    return (
      <button
        type="button"
        onClick={() => !isDisabled && onClick()}
        disabled={isDisabled}
        // Name the action, not the card: without this the kicker ("Option"),
        // the file-format hint, and the "Choose" chip all read as its name.
        aria-label={isProcessing ? "Preparing photos..." : displayLabel}
        aria-describedby={uploadHintId}
        data-reference-upload-widget
        className={cn(
          "group/action relative w-full min-w-0 touch-manipulation overflow-hidden rounded-xl border border-dashed px-3 py-2.5 text-left shadow-sm transition-all duration-200 sm:rounded-2xl sm:p-4",
          "border-brand-logo/35 bg-gradient-to-br from-brand-logo/10 via-background to-muted/45",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          "hover:-translate-y-0.5 hover:border-brand-logo/60 hover:shadow-md motion-reduce:hover:translate-y-0",
          isSelected && "border-solid border-brand-logo/55 bg-brand-logo/10",
          isDisabled &&
            !isSelected &&
            "cursor-default opacity-50 shadow-none hover:translate-y-0 hover:border-brand-logo/35 hover:shadow-none",
          className
        )}
      >
        <span className="flex min-w-0 items-center gap-2.5 sm:gap-3.5">
          <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-background text-brand-logo shadow-sm ring-1 ring-brand-logo/20 sm:size-12 sm:rounded-xl">
            {isProcessing ? (
              <LoadingSpinner size="sm" color="gray" className="shrink-0" />
            ) : choiceVisual ? (
              choiceVisual
            ) : (
              meta.icon
            )}
          </span>

          <span className="min-w-0 flex-1">
            <span className="hidden text-[11px] font-semibold uppercase tracking-wide text-brand-logo sm:block">
              {meta.kicker}
            </span>
            <span className="mt-0.5 block text-sm font-semibold leading-snug text-foreground [overflow-wrap:anywhere]">
              {isProcessing ? "Preparing photos..." : displayLabel}
            </span>
            <span
              id={uploadHintId}
              className="mt-1 hidden text-xs leading-snug text-muted-foreground sm:block"
            >
              {REFERENCE_IMAGE_FORMAT_LABEL} · up to 5 MB each ·{" "}
              {REFERENCE_IMAGE_MAX_COUNT} images max
            </span>
          </span>

          <span
            className={cn(
              "flex shrink-0 items-center gap-1 rounded-full bg-background px-2 py-1.5 text-xs font-semibold text-foreground shadow-sm ring-1 ring-border transition-transform duration-200 sm:px-2.5",
              !isDisabled && "group-hover/action:translate-x-0.5",
              isDisabled && !isSelected && "opacity-0"
            )}
          >
            {isSelected ? (
              <>
                <CheckIcon size={13} weight="bold" aria-hidden="true" />
                Added
              </>
            ) : (
              <>
                Choose
                <ArrowRightIcon size={13} weight="bold" aria-hidden="true" />
              </>
            )}
          </span>
        </span>
      </button>
    );
  }

  if (isVibePreset) {
    return (
      <button
        type="button"
        onClick={() => !isDisabled && onClick()}
        disabled={isDisabled}
        {...(isSelector ? { "aria-pressed": isSelected } : {})}
        data-vibe-preset-widget
        className={cn(
          "group/action relative min-w-0 touch-manipulation overflow-hidden rounded-xl border p-1.5 text-left transition-all duration-200",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          "border-border bg-background shadow-sm hover:-translate-y-0.5 hover:border-brand-logo/35 hover:bg-muted/25 hover:shadow-md motion-reduce:hover:translate-y-0",
          isSelected &&
            "border-brand-logo/50 bg-brand-logo/10 opacity-100 shadow-sm hover:border-brand-logo/50 hover:bg-brand-logo/10",
          isDisabled &&
            !isSelected &&
            "cursor-default opacity-50 shadow-none hover:border-border hover:bg-background",
          className
        )}
      >
        <span className="flex min-h-[76px] min-w-0 items-stretch gap-2">
          <span
            data-vibe-icon-pane
            className="flex w-[86px] shrink-0 items-center justify-center rounded-lg p-1"
          >
            <span className="block size-full min-h-0 min-w-0">
              {isProcessing ? (
                <span className="flex size-full items-center justify-center rounded-lg bg-muted">
                  <LoadingSpinner size="sm" color="gray" className="shrink-0" />
                </span>
              ) : (
                choiceVisual
              )}
            </span>
          </span>

          <span
            data-vibe-selector-pane
            className="flex min-w-0 flex-1 items-center justify-between gap-2 px-2 py-2"
          >
            <span className="min-w-0">
              <span className="block text-[11px] font-semibold uppercase leading-none text-muted-foreground">
                Vibe
              </span>
              <span className="mt-1 block min-w-0 text-sm font-semibold leading-snug text-foreground [overflow-wrap:anywhere]">
                {isProcessing ? "Updating..." : displayLabel}
              </span>
            </span>

            <span
              className={cn(
                "flex size-7 shrink-0 items-center justify-center rounded-full ring-1 transition-transform duration-200",
                isSelected
                  ? "bg-brand-logo text-brand-logo-foreground ring-brand-logo"
                  : "bg-background text-muted-foreground ring-border group-hover/action:translate-x-0.5 group-hover/action:text-foreground",
                isDisabled && !isSelected && "opacity-0"
              )}
            >
              {isSelected ? (
                <CheckIcon size={14} weight="bold" aria-hidden="true" />
              ) : isSelector ? (
                <span className="size-2.5 rounded-full border border-current" />
              ) : (
                <ArrowRightIcon size={14} weight="bold" aria-hidden="true" />
              )}
            </span>
          </span>
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => !isDisabled && onClick()}
      disabled={isDisabled}
      {...(showsRadio ? { "aria-pressed": isSelected } : {})}
      className={cn(
        "group/action relative min-w-0 touch-manipulation rounded-xl border text-left transition-all duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        meta.isCommitAction
          ? "w-full p-3"
          : compact
            ? "min-h-[52px] p-2"
            : "min-h-[64px] p-2.5",
        isCredit
          ? "border-brand-logo/40 bg-brand-logo/10 shadow-sm hover:-translate-y-0.5 hover:border-brand-logo/60 hover:bg-brand-logo/15 hover:shadow-md motion-reduce:hover:translate-y-0"
          : "border-border bg-background shadow-sm hover:-translate-y-0.5 hover:border-brand-logo/35 hover:bg-muted/35 hover:shadow-md motion-reduce:hover:translate-y-0",
        isSelected &&
          "border-brand-logo/50 bg-brand-logo/10 opacity-100 shadow-sm hover:translate-y-0 hover:border-brand-logo/50 hover:bg-brand-logo/10 hover:shadow-sm",
        isDisabled &&
          !isSelected &&
          "cursor-default opacity-50 shadow-none hover:translate-y-0 hover:border-border hover:bg-background hover:shadow-none",
        className
      )}
    >
      <div
        className={cn(
          "flex min-w-0 items-center",
          compact ? "gap-2.5" : "gap-3"
        )}
      >
        <span
          className={cn(
            "flex shrink-0 items-center justify-center rounded-lg",
            meta.isCommitAction
              ? "size-9"
              : choiceVisual
                ? compact
                  ? "size-9"
                  : "size-11"
                : "size-8",
            choiceVisual ? "bg-transparent p-0" : getIconClass(meta.tone)
          )}
        >
          {isProcessing ? (
            <LoadingSpinner
              size="sm"
              color={isCredit ? "white" : "gray"}
              className="shrink-0"
            />
          ) : choiceVisual ? (
            choiceVisual
          ) : (
            meta.icon
          )}
        </span>

        <span className="min-w-0 flex-1 space-y-1">
          <span className="flex min-w-0 items-center gap-2">
            <span className="block min-w-0 flex-1 break-words text-sm font-semibold leading-snug text-foreground">
              {isProcessing ? "Updating..." : displayLabel}
            </span>
            {meta.badge && meta.isCommitAction && (
              <span
                className={cn(
                  "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none ring-1",
                  getBadgeClass(meta.tone)
                )}
              >
                {meta.badge}
              </span>
            )}
          </span>
          {meta.isCommitAction && (
            <span className="block text-xs font-medium leading-snug text-muted-foreground">
              {meta.kicker}
            </span>
          )}
        </span>

        {isSelected ? (
          <CheckIcon
            size={16}
            weight="bold"
            className="shrink-0 text-brand-logo"
            aria-hidden="true"
          />
        ) : showsRadio ? (
          // Selectors show a radio affordance — the arrow is reserved for
          // buttons that act (fire an agent turn) when clicked; upload rows
          // act (open the picker), so they keep the arrow.
          <span
            className={cn(
              "flex size-5 shrink-0 items-center justify-center rounded-full ring-1 ring-border text-muted-foreground",
              isDisabled && "opacity-0"
            )}
            aria-hidden="true"
          >
            <span className="size-2 rounded-full border border-current" />
          </span>
        ) : (
          <ArrowRightIcon
            size={16}
            weight="bold"
            className={cn(
              "shrink-0 text-muted-foreground transition-transform duration-200",
              !isDisabled && "group-hover/action:translate-x-0.5",
              isDisabled && "opacity-0"
            )}
            aria-hidden="true"
          />
        )}
      </div>
    </button>
  );
}
